import { supabase } from '../lib/supabase';
import { getLocalUser } from '../utils/authHelpers';

type AgentType = 'call' | 'whatsapp' | 'email' | 'linkedin';

interface TestResult {
  success: boolean;
  message: string;
  type?: 'direct_message' | 'connection_request';
}

async function getOrCreateTestAgent(clientId: string, userId: string) {
  const { data: existing } = await supabase
    .from('test_agents')
    .select('*')
    .eq('client_id', clientId)
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) return existing;

  const [userSettingsRes, salesMaterialsRes] = await Promise.all([
    supabase.from('user_settings').select('*').eq('client_id', clientId).eq('user_id', userId).maybeSingle(),
    supabase.from('sales_materials').select('onboarding_input, about_company').eq('client_id', clientId).maybeSingle(),
  ]);

  const us = userSettingsRes.data;
  const sm = salesMaterialsRes.data;
  const onboarding = sm?.onboarding_input || {};
  const aboutCompany = sm?.about_company || {};

  const newRow = {
    client_id: clientId,
    user_id: userId,
    company_name: us?.company_name || aboutCompany?.company_name || '',
    company_website: us?.company_website || aboutCompany?.company_website || '',
    country: us?.country || '',
    time_zone: us?.time_zone || 'UTC',
    company_industry: us?.company_industry || onboarding?.industry || '',
    company_type: onboarding?.company_type || '',
    products_services: onboarding?.products_services || aboutCompany?.products_services || '',
    full_name: us?.full_name || 'Test Prospect',
    job_title: us?.job_title || 'Decision Maker',
  };

  const { data: inserted, error } = await supabase
    .from('test_agents')
    .insert(newRow)
    .select()
    .maybeSingle();

  if (error) throw new Error(`Failed to create test agent row: ${error.message}`);
  return inserted;
}

async function updateTestAgentContact(clientId: string, userId: string, agentType: AgentType, contactValue: string) {
  const updateFields: Record<string, string> = { updated_at: new Date().toISOString() };

  if (agentType === 'call' || agentType === 'whatsapp') {
    updateFields.phone_number = contactValue;
  } else if (agentType === 'email') {
    updateFields.email_address = contactValue;
  } else if (agentType === 'linkedin') {
    updateFields.linkedin_profile_url = contactValue;
  }

  await supabase
    .from('test_agents')
    .update(updateFields)
    .eq('client_id', clientId)
    .eq('user_id', userId);
}

export async function executeTest(agentType: AgentType, contactInput: string): Promise<TestResult> {
  const localUser = getLocalUser();
  if (!localUser?.client_id || !localUser?.email) {
    return { success: false, message: 'Not authenticated. Please log in again.' };
  }

  const clientId = localUser.client_id;
  const userId = localUser.email;

  const sanitizedInput = (agentType === 'call' || agentType === 'whatsapp')
    ? contactInput.replace(/\D/g, '')
    : contactInput;

  await getOrCreateTestAgent(clientId, userId);
  await updateTestAgentContact(clientId, userId, agentType, sanitizedInput);

  const functionName = agentType === 'call' ? 'send-test-call' : 'test-agent-send';
  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;

  const payload = agentType === 'call'
    ? { client_id: clientId, user_id: userId, contact_input: sanitizedInput }
    : { client_id: clientId, user_id: userId, channel: agentType, contact_input: sanitizedInput };

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json().catch(() => null);

  if (!response.ok) {
    const errorMsg = result?.error || `Server returned status ${response.status}. Please try again.`;
    return { success: false, message: errorMsg };
  }

  if (result.success) {
    if (agentType === 'linkedin' && result.type === 'connection_request') {
      return { success: true, message: 'Connection request sent successfully.', type: 'connection_request' };
    }
    const labels: Record<AgentType, string> = {
      call: 'Test call initiated',
      whatsapp: 'Test WhatsApp message sent',
      email: 'Test email sent',
      linkedin: 'Test LinkedIn message sent',
    };
    return { success: true, message: `${labels[agentType]}. You should receive it shortly.`, type: result.type };
  }

  return { success: false, message: result.error || 'Test failed. Please try again.' };
}
